// Dragging the full-screen chat down by its header closes it (phones, docs/022).
// The sheet is this frame, drawn by embed.js, so the frame only measures the
// finger and the page moves the sheet (protocol "drag").
//
// Measured on iOS 26 (simulator, 01.10): screenY follows the finger evenly
// while clientY jumps, because the frame itself travels with the sheet a frame
// behind; and pointerup arrives with garbage coordinates (screenY 0). So the
// finger is read from screenY, and a release ends where the last move was.
// Where screenY disagrees with the first unmoved clientY delta (headless
// WebKit reports it upside down) the finger falls back to clientY plus the
// offset already sent.

import { DRAG_THRESHOLD, releaseVelocity, type Sample } from "../../shared/sheet.ts";
import { toParent } from "./bridge.ts";

/** Starts listening on the header; returns the clean-up. */
export function attachSheetDrag(handle: HTMLElement): () => void {
  let grab: { id: number; clientX: number; clientY: number; screenY: number } | null = null;
  let dragging = false;
  let useScreen = true;
  let samples: Sample[] = [];
  /** The last dy sent: where the sheet is, and where a release ends. */
  let sent = 0;
  // A drag that ends over "Nowa rozmowa" must not also press it.
  let swallowClick = false;

  const fullscreen = () => document.documentElement.hasAttribute("data-fullscreen");
  const fingerDy = (event: PointerEvent) => (useScreen ? event.screenY - grab!.screenY : event.clientY + sent - grab!.clientY);

  function down(event: PointerEvent) {
    if (!fullscreen() || !event.isPrimary || (event.pointerType === "mouse" && event.button !== 0)) return;
    grab = { id: event.pointerId, clientX: event.clientX, clientY: event.clientY, screenY: event.screenY };
    dragging = false;
    sent = 0;
    samples = [];
  }

  function move(event: PointerEvent) {
    if (!grab || event.pointerId !== grab.id) return;
    if (!dragging) {
      // Nothing has moved the frame yet, so clientY is honest here.
      const dy = event.clientY - grab.clientY;
      const dx = event.clientX - grab.clientX;
      if (Math.abs(dy) < DRAG_THRESHOLD && Math.abs(dx) < DRAG_THRESHOLD) return;
      // Upwards or sideways is not a close: let it go.
      if (dy <= 0 || Math.abs(dx) > Math.abs(dy)) {
        grab = null;
        return;
      }
      useScreen = Math.abs(event.screenY - grab.screenY - dy) <= 2;
      dragging = true;
      try {
        handle.setPointerCapture(event.pointerId);
      } catch {
        // the pointer is already gone: the drag ends with pointerup/cancel anyway
      }
    }
    sent = Math.max(0, fingerDy(event));
    samples.push({ y: sent, t: event.timeStamp });
    if (samples.length > 32) samples = samples.slice(-16);
    toParent({ type: "drag", phase: "move", dy: sent, velocity: 0 });
  }

  function end(event: PointerEvent) {
    if (!grab || event.pointerId !== grab.id) return;
    if (dragging) {
      const cancelled = event.type === "pointercancel";
      toParent({ type: "drag", phase: cancelled ? "cancel" : "end", dy: sent, velocity: cancelled ? 0 : releaseVelocity(samples) });
      swallowClick = !cancelled;
      window.setTimeout(() => (swallowClick = false), 0);
    }
    grab = null;
    dragging = false;
  }

  function click(event: MouseEvent) {
    if (!swallowClick) return;
    swallowClick = false;
    event.stopPropagation();
    event.preventDefault();
  }

  handle.addEventListener("pointerdown", down);
  handle.addEventListener("pointermove", move);
  handle.addEventListener("pointerup", end);
  handle.addEventListener("pointercancel", end);
  handle.addEventListener("click", click, true);
  return () => {
    handle.removeEventListener("pointerdown", down);
    handle.removeEventListener("pointermove", move);
    handle.removeEventListener("pointerup", end);
    handle.removeEventListener("pointercancel", end);
    handle.removeEventListener("click", click, true);
  };
}
